import React, { useEffect, useState } from 'react';
import './AccountSubscription.scss';
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { useCurrentStatus } from '../hooks/useCurrentStatus';
import LoadingSpinner from '../components/LoadingSpinner';
import Feedback from '../components/Feedback';
import { cancel } from '../services/account.service';
import { formatDate } from '../helpers/dates';
import { useToast } from '../contexts/ToastContext';

const AccountSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [confirmFree, setConfirmFree] = useState(false);
  const [isFreeLoading, setIsFreeLoading] = useState(false);

  const [freeError, setFreeError] = useState<string[]>([]);

  const { data, loading, error, refetch } = useCurrentStatus();

  const accountLevel = data?.accountLevel;

  const [selectedAccountLevel, setSelectedAccountLevel] =
    useState(accountLevel);

  const { showToast } = useToast();

  const handleChangeFree = async () => {
    setIsFreeLoading(true);

    const result = await cancel();
    if (!result.success) {
      setFreeError(result.errors);
    } else {
      setFreeError([]);
      await refetch();
      showToast('Subscription updated', 'success');
    }

    setIsFreeLoading(false);
    setConfirmFree(false);
  };

  const changePlan = () => {
    if (selectedAccountLevel !== 'Free') {
      navigate(`/checkout?accountLevel=${selectedAccountLevel}`);
    }

    if (selectedAccountLevel === 'Free') {
      setConfirmFree(true);
    }
  };

  useEffect(() => {
    if (accountLevel) {
      setSelectedAccountLevel(accountLevel);
    }
  }, [data]);

  const planItem = (level: string) => {
    const price = level === 'Free' ? '0' : level === 'Basic' ? '5' : '30';
    const description =
      level === 'Free'
        ? 'Perfect for trying out and running small sessions.'
        : level === 'Basic'
          ? 'Ideal for regular presenters needing more flexibility.'
          : 'For professionals & organisations wanting full customisation.';
    const points =
      level === 'Free' ? (
        <>
          <li>Create, preview & present sessions</li>
          <li>Multi-factor authentication</li>
          <li>Custom colours & fonts</li>
          <li>Up to 3 sessions</li>
        </>
      ) : level === 'Basic' ? (
        <>
          <li>Everything in Free</li>
          <li>Unlimited sessions</li>
          <li>Multiple showings</li>
          <li>Custom welcome branding</li>
        </>
      ) : (
        <>
          <li>Everything in Basic</li>
          <li>Custom welcome & end pages</li>
          <li>Unlimited attendees</li>
        </>
      );
    return (
      <li className="plan">
        <input
          id={`plan-${level}`}
          name="plan"
          type="radio"
          value={level}
          className="plan__input"
          checked={selectedAccountLevel === level}
          onChange={() => setSelectedAccountLevel(level)}
        />
        <label
          className={`plan__card ${selectedAccountLevel === level ? 'plan__card--current' : ''}`}
          htmlFor={`plan-${level}`}
          aria-label={`Select ${level} plan`}
        >
          <div className="plan__content">
            <div className="plan__header">
              <h2 className="plan__name">{level}</h2>
              <p className="plan__price">
                £{price}
                <span className="plan__price-period">/mo</span>
              </p>
            </div>
            <p className="plan__desc">{description}</p>
            <ul className="plan__bullets" role="list">
              {points}
            </ul>
            {accountLevel === level && (
              <span className="plan__badge" aria-hidden="true">
                Current
              </span>
            )}
            {data?.pendingAccountLevelEnum === level && (
              <span className="plan__badge" aria-hidden="true">
                Pending
              </span>
            )}
          </div>
        </label>
      </li>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !data) {
    return (
      <Feedback
        message={error ?? 'Unable to find current subscription'}
        type="error"
      />
    );
  }

  return (
    <>
      <h2>Account Subscription</h2>
      <section
        id="subscription"
        className="subscription"
        aria-labelledby="subscription-title"
        data-current-plan="basic"
      >
        <header className="subscription__header">
          <p className="subscription__current">
            Current plan: <strong>{accountLevel}</strong>
            <span className="badge" aria-hidden="true">
              Current
            </span>
          </p>
          {data.pendingAccountLevelEnum &&
            accountLevel !== data.pendingAccountLevelEnum && (
              <p className="subscription__current">
                Pending plan: <strong>{data.pendingAccountLevelEnum}</strong>
                <span className="badge" aria-hidden="true">
                  Pending
                </span>
              </p>
            )}
          {data.subscriptionCancelAtUtc && (
            <p className="subscription__notice subscription__notice--warning">
              Your subscription is scheduled to end on{' '}
              <strong>{formatDate(data.subscriptionCancelAtUtc)}</strong>. You
              will be moved to the Free plan at that time.
            </p>
          )}
        </header>

        <form
          className="subscription__form"
          action="/api/billing/change-plan"
          method="post"
        >
          <fieldset className="plans" aria-labelledby="plan-legend">
            <legend id="plan-legend">Choose a plan</legend>

            <ul className="plans__list" role="list">
              {planItem('Free')}
              {planItem('Basic')}
              {planItem('Premium')}
            </ul>
          </fieldset>

          {accountLevel !== selectedAccountLevel &&
            selectedAccountLevel !== 'Free' && (
              <div className="subscription__actions">
                <div className="button-container single-button">
                  <Button
                    onClick={changePlan}
                    disabled={!!data.pendingAccountLevelEnum}
                  >
                    Update plan
                  </Button>
                </div>
                <p className="subscription__note" role="note">
                  {data.pendingAccountLevelEnum
                    ? 'You cannot change your subscription whilst you have a pending plan.  Please wait for your new plan to be applied before making any further changes.'
                    : 'Changes take effect immediately. You can cancel anytime.'}
                </p>
              </div>
            )}

          {accountLevel !== selectedAccountLevel &&
            selectedAccountLevel === 'Free' && (
              <div className="subscription__actions">
                <div className="button-container single-button">
                  <Button onClick={changePlan}>Cancel subscription</Button>
                </div>
                <p className="subscription__note" role="note">
                  Changes take effect immediately. You will not be charged
                  again.
                </p>
              </div>
            )}
        </form>

        <Feedback messages={freeError} type="error" />

        <section className="comparison" aria-labelledby="comparison-title">
          <h2 id="comparison-title">Feature comparison</h2>

          <table className="matrix">
            <caption className="visually-hidden">
              Plans compared across features
            </caption>
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">
                  Free
                  {accountLevel === 'Free' && (
                    <>
                      {' '}
                      <span className="badge" aria-hidden="true">
                        Current
                      </span>
                      <span className="visually-hidden">(current)</span>
                    </>
                  )}
                </th>
                <th scope="col">
                  Basic
                  {accountLevel === 'Basic' && (
                    <>
                      {' '}
                      <span className="badge" aria-hidden="true">
                        Current
                      </span>
                      <span className="visually-hidden">(current)</span>
                    </>
                  )}
                </th>
                <th scope="col">
                  Premium
                  {accountLevel === 'Premium' && (
                    <>
                      {' '}
                      <span className="badge" aria-hidden="true">
                        Current
                      </span>
                      <span className="visually-hidden">(current)</span>
                    </>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Create sessions</th>
                <td>✔</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Preview sessions</th>
                <td>✔</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Present sessions</th>
                <td>✔</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Multi-factor authentication</th>
                <td>✔</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Custom colour scheme</th>
                <td>✔</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Font selection</th>
                <td>✔</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Multiple showings</th>
                <td>—</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Welcome page logo</th>
                <td>—</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Welcome page background</th>
                <td>—</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">End feedback link</th>
                <td>—</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">End subscribe link</th>
                <td>—</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">End donation link</th>
                <td>—</td>
                <td>✔</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Welcome custom page</th>
                <td>—</td>
                <td>—</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">End custom pages</th>
                <td>—</td>
                <td>—</td>
                <td>✔</td>
              </tr>
              <tr>
                <th scope="row">Max sessions</th>
                <td>3</td>
                <td>Unlimited</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <th scope="row">Max attendees per session</th>
                <td>15</td>
                <td>100</td>
                <td>Unlimited</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Price</th>
                <td>£0/mo</td>
                <td>£5/mo</td>
                <td>£30/mo</td>
              </tr>
            </tfoot>
          </table>
        </section>
      </section>
      {confirmFree && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>{'Are you sure you want to change to the free plan?'}</p>
            <div className="button-container">
              <Button
                isLoading={isFreeLoading}
                onClick={handleChangeFree}
                className="confirm-button"
                disabled={false}
                isSubmit={false}
              >
                Yes
              </Button>
              <Button type="Outline" onClick={() => setConfirmFree(false)}>
                No
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountSubscription;
